import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import get from 'lodash/get';
import Header from '../../components/Header';
import css from './App.module.scss';
import { AnalyticsContext } from '../../ModularReduxAnalytics';
import { routes, nextGenRoutes } from './routes';
import { useApp, useAppDispatchers } from '../../state/app/index.ts';
import { useUser, useUserDispatchers } from '../../state/user/index.ts';
import Toast from '../../components/UI/Toast/index.tsx';
import { useToggles } from '../../state/toggles/index.ts';
import { redirect } from '../../helpers/redirect.ts';

const AppComponent = props => {
  const history = useHistory();
  const { flowId, completeOnInit, showFccPreReg } = useApp();
  const { clearError } = useAppDispatchers();
  const { setNextRegStep } = useUserDispatchers();
  const {
    role,
    partner,
    promotionId,
    registeredUser,
    nextRegStep,
    referralRewardValue,
    advocateIsTeen,
    userIsTeen,
    dob
  } = useUser();
  const location = useLocation();
  const { segment } = useContext(AnalyticsContext);
  const {
    growthRegistrationFlowFields,
    kidFirstRegParentInviteV0,
    kidFirstRegParentInviteV1
  } = useToggles();
  const [banner, setBanner] = useState();
  const [rewardValue, setRewardValue] = useState(0);
  const [showBanner, setShowBanner] = useState(false);

  const getRouteFromNextStep = () => {
    switch (nextRegStep) {
      case 'approver_declined':
        return routes.approverDeclined;
      case 'confirm-role':
        setShowBanner(true);
        if (showFccPreReg) {
          return routes.choosePlan;
        }
        return routes.chooseRole;
      case 'validate':
        return routes.confirmCode;
      case 'login':
        return routes.login;
      case 'register':
        return routes.regFlow;
      case 'parent_verify_declined':
        return routes.verifyDeclined;
      case 'complete':
        if (
          props.extras ||
          (!completeOnInit && (role === 'parent' || role === 'approver'))
        ) {
          return routes.regFlow;
        }
        return routes.complete;
      case 'child_pending_parent_completion':
      case 'complete_child':
        if (
          kidFirstRegParentInviteV0.enabled ||
          kidFirstRegParentInviteV1.enabled
        ) {
          return routes.parentInviteComplete;
        }
        if (!props.extras) {
          return routes.childComplete;
        }
        return;
      case 'cancel_age':
        return routes.needParent;
      case 'complete_placeholder':
        return routes.enterDob;
      case 'forgot_password':
        return routes.forgotPassword;
      case 'next_gen_onboarding':
        return nextGenRoutes.onboarding;
      case 'parent_invite':
        return routes.parentInvite;
      case 'parent_invite_complete':
        return routes.parentInviteComplete;
      default:
        if (role || get(registeredUser, 'id')) {
          return routes.regFlow;
        }

        return routes.baseRoute;
    }
  };

  const getBackTo = pathname => {
    switch (pathname) {
      case '/forgot-password':
        return { url: '/login', nextRegStep: 'login' };
      case '/confirm-code':
        return { url: '/choose-role', nextRegStep: 'confirm-role' };
      case '/enter-dob':
        return { url: '/choose-role', nextRegStep: 'confirm-role' };
      default:
        return null;
    }
  };

  const isUserATeen = () => {
    if (role === 'parent' || dob) {
      return userIsTeen;
    }
    return advocateIsTeen;
  };

  const showNewBanner = () => {
    const {
      new_banner_head: newBannerHeader,
      new_banner_sub_head: newBannerBody
    } = growthRegistrationFlowFields?.variables;
    return (
      <Toast
        heading={newBannerHeader}
        message={newBannerBody}
        rewardValue={rewardValue}
      />
    );
  };

  useEffect(() => {
    setRewardValue(
      !Number.isNaN(referralRewardValue) ? referralRewardValue : 0
    );
  }, [referralRewardValue]);

  useEffect(() => {
    const isTeen = isUserATeen();
    if (
      rewardValue > 0 &&
      growthRegistrationFlowFields?.variables?.show_new_banner &&
      isTeen &&
      showBanner
    ) {
      setBanner(showNewBanner);
    } else {
      setBanner(null);
    }
  }, [rewardValue, growthRegistrationFlowFields, showBanner, userIsTeen, role]);

  useEffect(() => {
    if (segment)
      segment.identifyUser({
        flowId: flowId || undefined
      });
  }, []);

  useEffect(() => {
    // Don't want to clear out error if moving from reset password to login
    if (location.pathname.indexOf('reset-password') === -1) {
      clearError();
    }

    if (nextRegStep) {
      const route = getRouteFromNextStep();

      if (route.startsWith('http')) {
        redirect(route);
      } else {
        history.replace(route);
      }
    }
  }, [nextRegStep]);

  return (
    <div className={css.App}>
      <Header
        backTo={getBackTo(location.pathname)}
        setNextRegStep={setNextRegStep}
        location={location}
        promotionId={promotionId}
        partner={partner}
        data-e2e="header"
      />
      {banner}

      {props.children}
      <footer style={{ padding: '5px', textAlign: 'right' }}>
        <div style={{ width: '100%', height: '60px' }} />
      </footer>
    </div>
  );
};

export default React.memo(AppComponent);
